import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useArticleCategories, useCreateCategory, useUpdateCategory, useDeleteCategory,
  useArticles,
} from '@/hooks/use-articles';
import { ImageUpload } from '@/components/ui/image-upload';
import {
  Button, Card, CardContent, CardHeader,
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
  EmptyState, FilterBar, FilterField, Input, Label, PageHeader,
  Skeleton,
} from '@erp/ui';
import { Plus, Trash2, Pencil, ChevronRight, FolderOpen, Search } from '@/components/icons';
import { ArticleAssignDialog } from './ArticleAssignDialog';
import type { ArticleCategory } from '@erp/data-access/interfaces';

export function CategoriesPage() {
  const { t } = useTranslation();
  const { data: categories, isLoading } = useArticleCategories();
  const { data: articles } = useArticles();
  const createMutation = useCreateCategory();
  const updateMutation = useUpdateCategory();
  const deleteMutation = useDeleteCategory();

  const [search, setSearch] = useState('');

  // Create dialog state
  const [formOpen, setFormOpen] = useState(false);
  const [catName, setCatName] = useState('');
  const [catImage, setCatImage] = useState<string | null>(null);
  const [subNames, setSubNames] = useState<string[]>(['']);

  // Edit dialog state
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState<ArticleCategory | null>(null);
  const [editName, setEditName] = useState('');
  const [editImage, setEditImage] = useState<string | null>(null);

  // Assign dialog state
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignTarget, setAssignTarget] = useState<ArticleCategory | null>(null);

  const [newSubName, setNewSubName] = useState<Record<string, string>>({});
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  // Count articles per category/subcategory
  const articleCountMap = useMemo(() => {
    const map = new Map<string, number>();
    for (const a of articles ?? []) {
      if (a.categoryId) map.set(a.categoryId, (map.get(a.categoryId) ?? 0) + 1);
    }
    return map;
  }, [articles]);

  // Total articles for a root category = direct + all children
  const totalArticles = (catId: string) => {
    let count = articleCountMap.get(catId) ?? 0;
    for (const c of (categories ?? []).filter((c) => c.parentId === catId)) {
      count += articleCountMap.get(c.id) ?? 0;
    }
    return count;
  };

  const rootCategories = (categories ?? []).filter((c) => !c.parentId);
  const getChildren = (parentId: string) => (categories ?? []).filter((c) => c.parentId === parentId);

  // Filter by search
  const filteredRoots = useMemo(() => {
    if (!search.trim()) return rootCategories;
    const s = search.toLowerCase();
    return rootCategories.filter((cat) => {
      if (cat.name.toLowerCase().includes(s)) return true;
      return getChildren(cat.id).some((sub) => sub.name.toLowerCase().includes(s));
    });
  }, [rootCategories, search, categories]);

  const handleCreate = async () => {
    if (!catName.trim()) return;
    const created = await createMutation.mutateAsync({ name: catName.trim(), imageUrl: catImage ?? undefined });
    for (const sub of subNames) {
      if (sub.trim()) await createMutation.mutateAsync({ name: sub.trim(), parentId: created.id });
    }
    setCatName(''); setCatImage(null); setSubNames(['']); setFormOpen(false);
  };

  const openEdit = (cat: ArticleCategory) => {
    setEditing(cat);
    setEditName(cat.name);
    setEditImage(cat.imageUrl);
    setEditOpen(true);
  };

  const handleEdit = async () => {
    if (!editing || !editName.trim()) return;
    await updateMutation.mutateAsync({ id: editing.id, input: { name: editName.trim(), imageUrl: editImage ?? undefined } });
    setEditOpen(false); setEditing(null);
  };

  const handleAddSub = async (parentId: string) => {
    const name = newSubName[parentId]?.trim();
    if (!name) return;
    await createMutation.mutateAsync({ name, parentId });
    setNewSubName((prev) => ({ ...prev, [parentId]: '' }));
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('articles.categories.title')}
        actions={
          <Button onClick={() => { setCatName(''); setCatImage(null); setSubNames(['']); setFormOpen(true); }}>
            <Plus data-icon="inline-start" size={16} />
            {t('articles.categories.newCategory')}
          </Button>
        }
      />

      <Card className="transition-all duration-200">
        <CardHeader className="pb-3">
          <FilterBar onClear={search ? () => setSearch('') : undefined} clearLabel={t('common.clearFilters')}>
            <FilterField label={t('common.search')} grow>
              <div className="relative">
                <Search size={14} className="absolute top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none start-2.5" />
                <Input placeholder={t('common.search')} value={search} onChange={(e) => setSearch(e.target.value)} className="ps-8 h-8" />
              </div>
            </FilterField>
          </FilterBar>
        </CardHeader>
        <CardContent className="pt-0">
      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="rounded-lg border bg-card px-4 py-3 flex items-center gap-3">
              <Skeleton className="w-10 h-10 rounded-lg" />
              <Skeleton className="h-4 w-48" />
            </div>
          ))}
        </div>
      ) : filteredRoots.length === 0 ? (
        <EmptyState icon={<FolderOpen size={18} />} title={t('common.noResults')} action={<Button variant="outline" size="sm" onClick={() => { setCatName(''); setCatImage(null); setSubNames(['']); setFormOpen(true); }}>
            <Plus data-icon="inline-start" size={14} />
            {t('articles.categories.newCategory')}
          </Button>
        } />
      ) : (
        <div className="space-y-2">
          {filteredRoots.map((cat) => {
            const children = getChildren(cat.id);
            const isOpen = expanded.has(cat.id);
            const catArticleCount = totalArticles(cat.id);
            const toggle = () => setExpanded((prev) => {
              const next = new Set(prev);
              if (next.has(cat.id)) next.delete(cat.id); else next.add(cat.id);
              return next;
            });

            return (
              <div key={cat.id} className="rounded-lg border bg-card">
                <div
                  className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-accent/50 transition-colors"
                  onClick={toggle}
                >
                  <div className="flex items-center gap-3">
                    <ChevronRight size={16} className={`text-muted-foreground transition-transform shrink-0 ${isOpen ? 'rotate-90' : ''}`} />
                    {cat.imageUrl ? (
                      <img src={cat.imageUrl} alt="" className="w-10 h-10 rounded-lg object-cover shrink-0" />
                    ) : (
                      <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center shrink-0">
                        <FolderOpen size={18} className="text-primary" />
                      </div>
                    )}
                    <div>
                      <span className="font-medium">{cat.name}</span>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        {children.length > 0 && <span>{children.length} {t('articles.subCategory').toLowerCase()}</span>}
                        {catArticleCount > 0 && <span>{catArticleCount} {t('dashboard.articles')}</span>}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="outline" size="sm" className="text-xs h-7" onClick={(e) => { e.stopPropagation(); setAssignTarget(cat); setAssignOpen(true); }}>
                      <Plus size={12} className="me-1" />{t('articles.assignArticle')}
                    </Button>
                    <Button variant="ghost" size="icon-xs" onClick={(e) => { e.stopPropagation(); openEdit(cat); }}>
                      <Pencil size={14} />
                    </Button>
                    <Button variant="ghost" size="icon-xs" onClick={(e) => { e.stopPropagation(); deleteMutation.mutate(cat.id); }}>
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>

                {isOpen && (
                  <div className="border-t">
                    {children.length > 0 && (
                      <div className="px-4 py-2 space-y-1">
                        {children.map((sub) => {
                          const subCount = articleCountMap.get(sub.id) ?? 0;
                          return (
                            <div key={sub.id} className="flex items-center justify-between py-1.5 ps-10">
                              <div className="flex items-center gap-2">
                                <span className="text-sm">{sub.name}</span>
                                <span className="text-xs text-muted-foreground">({subCount} {t('dashboard.articles')})</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <Button variant="outline" size="sm" className="text-[11px] h-6 px-2" onClick={() => { setAssignTarget(sub); setAssignOpen(true); }}>
                                  <Plus size={10} className="me-1" />{t('articles.assignArticle')}
                                </Button>
                                <Button variant="ghost" size="icon-xs" onClick={() => openEdit(sub)}>
                                  <Pencil size={12} />
                                </Button>
                                <Button variant="ghost" size="icon-xs" onClick={() => deleteMutation.mutate(sub.id)}>
                                  <Trash2 size={12} />
                                </Button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    <div className={`px-4 py-2.5 ${children.length > 0 ? 'border-t' : ''}`}>
                      <div className="flex gap-2 ps-10">
                        <Input
                          placeholder={t('articles.subCategory')}
                          value={newSubName[cat.id] ?? ''}
                          onChange={(e) => setNewSubName((prev) => ({ ...prev, [cat.id]: e.target.value }))}
                          onKeyDown={(e) => { if (e.key === 'Enter') handleAddSub(cat.id); }}
                          className="h-8 text-sm flex-1 max-w-[300px]"
                        />
                        <Button variant="outline" size="sm"
                          disabled={!newSubName[cat.id]?.trim() || createMutation.isPending}
                          onClick={() => handleAddSub(cat.id)}
                        >
                          <Plus size={14} className="me-1.5" />{t('articles.subCategory')}
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
        </CardContent>
      </Card>

      {/* Create dialog */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('articles.categories.newCategory')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>{t('articles.category')} *</Label>
              <Input value={catName} onChange={(e) => setCatName(e.target.value)} autoFocus />
            </div>
            <div className="space-y-1.5">
              <Label>{t('articles.image')}</Label>
              <ImageUpload value={catImage} onChange={setCatImage} bucket="erp-images" pathPrefix="categories/" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">{t('articles.subCategory')}</Label>
              {subNames.map((sub, i) => (
                <div key={i} className="flex gap-2">
                  <Input
                    value={sub}
                    onChange={(e) => { const next = [...subNames]; next[i] = e.target.value; setSubNames(next); }}
                    placeholder={`${t('articles.subCategory')} ${i + 1}`}
                    className="flex-1"
                  />
                  {subNames.length > 1 && (
                    <Button type="button" variant="ghost" size="icon-xs" onClick={() => setSubNames(subNames.filter((_, j) => j !== i))}>
                      <Trash2 size={14} />
                    </Button>
                  )}
                </div>
              ))}
              <Button type="button" variant="outline" size="sm" onClick={() => setSubNames([...subNames, ''])}>
                <Plus size={12} className="me-1" />{t('articles.subCategory')}
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFormOpen(false)}>{t('common.cancel')}</Button>
            <Button onClick={handleCreate} disabled={!catName.trim() || createMutation.isPending}>
              {t('common.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{t('articles.categories.editCategory')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>{t('common.name')} *</Label>
              <Input value={editName} onChange={(e) => setEditName(e.target.value)} autoFocus />
            </div>
            {!editing?.parentId && (
              <div className="space-y-1.5">
                <Label>{t('articles.image')}</Label>
                <ImageUpload value={editImage} onChange={setEditImage} bucket="erp-images" pathPrefix="categories/" />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>{t('common.cancel')}</Button>
            <Button onClick={handleEdit} disabled={!editName.trim() || updateMutation.isPending}>
              {t('common.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign articles dialog */}
      {assignTarget && (
        <ArticleAssignDialog
          open={assignOpen}
          onOpenChange={setAssignOpen}
          field="categoryId"
          targetId={assignTarget.id}
          title={`${t('articles.assignArticle')} — ${assignTarget.name}`}
        />
      )}
    </div>
  );
}
